import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Building2,
  Briefcase,
  Check,
  CreditCard,
  Sparkles,
  ShieldCheck,
  ExternalLink,
  ArrowLeftRight,
} from '@/components/icons';
import { toast } from 'sonner';
import { SideDrawer } from '@/components/common/SideDrawer';
import { GlassCard } from '@/components/common/GlassCard';
import { Button } from '@/components/common/Button';
import { Badge } from '@/components/common/Badge';
import { useBillingStatus, useCreateCheckoutSession, useCustomerPortal } from '@/hooks/use-billing';
import { BillingInterval, PlanTier } from '@/types/billing';
import { estimateProMonthlyTotal, PLAN_PRICING } from '@/lib/pricing';

export interface PlanUpgradeDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  workspaceId?: string;
}

const BASIC_FEATURES = [
  'Property & unit directory with per-unit allocation weights',
  'Common expense tracking & allocation across units',
  'Bank statement CSV import & reconciliation',
  'Monthly fee posting & unit balance tracking',
  'Maintenance ticketing system',
  'Digital HOA voting & resident polls',
  'Community notices & announcements',
  'Resident self-service portal',
];

const PRO_FEATURES = [
  'Everything in Basic, across unlimited properties',
  'Multi-property switcher for portfolio-wide navigation',
  'Staff seats with role-based access (Manager, Auditor)',
  'Audit trail logging for all financial changes',
  'Priority support',
];

export const PlanUpgradeDrawer: React.FC<PlanUpgradeDrawerProps> = ({
  isOpen,
  onClose,
  workspaceId,
}) => {
  const { t } = useTranslation();
  const { data: billing } = useBillingStatus(workspaceId);
  const createCheckoutMutation = useCreateCheckoutSession();
  const customerPortalMutation = useCustomerPortal();
  const [interval, setInterval] = useState<BillingInterval>('monthly');
  const [selectedPlan, setSelectedPlan] = useState<PlanTier>('basic');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (billing?.billing_interval) {
      setInterval(billing.billing_interval);
    }
    if (billing?.plan_tier) {
      setSelectedPlan(billing.plan_tier);
    }
  }, [billing?.billing_interval, billing?.plan_tier]);

  const isTrialing = billing?.subscription_status === 'trialing';
  const isActive = billing?.subscription_status === 'active';
  const isCanceling = isActive && billing?.cancel_at_period_end;
  const isCanceled = billing?.subscription_status === 'canceled';
  const isUnpaid = billing?.subscription_status === 'unpaid';
  const isExpired = isCanceled || isUnpaid;
  const isBasic = billing?.plan_tier === 'basic';

  const handleCheckout = async () => {
    setLoading(true);
    try {
      const res = await createCheckoutMutation.mutateAsync({
        workspaceId,
        plan_tier: selectedPlan,
        billing_interval: selectedPlan === 'pro' ? 'monthly' : interval,
      });
      if (res.checkout_url) {
        window.location.href = res.checkout_url;
      }
    } catch (err: any) {
      toast.error(err.response?.data?.detail || t('billing.toast_checkout_failed', 'Failed to launch checkout session'));
    } finally {
      setLoading(false);
    }
  };

  const handleManageSubscription = async () => {
    try {
      const res = await customerPortalMutation.mutateAsync({
        workspaceId,
        returnUrl: window.location.href,
      });
      if (res.portal_url) {
        window.location.href = res.portal_url;
      }
    } catch (err: any) {
      toast.error(err.response?.data?.detail || t('billing.toast_portal_failed', 'Failed to open billing portal'));
    }
  };

  const planConfig = selectedPlan === 'pro'
    ? {
        name: 'Portfolio Pro',
        subtitle: 'Property Management',
        icon: <Briefcase className="w-4 h-4" />,
        price: `€${PLAN_PRICING.pro.monthlyPerUnit.toFixed(2)}`,
        priceUnit: '/ unit / month',
        priceNote: `€${estimateProMonthlyTotal(PLAN_PRICING.pro.minimumUnits)} / month minimum (includes ${PLAN_PRICING.pro.minimumUnits} units)`,
        features: PRO_FEATURES,
        featureLabel: 'Portfolio Features',
      }
    : {
        name: 'Basic',
        subtitle: 'Self-Managed',
        icon: <Building2 className="w-4 h-4" />,
        price: interval === 'annual' ? `€${PLAN_PRICING.basic.annual}` : `€${PLAN_PRICING.basic.monthly}`,
        priceUnit: interval === 'annual' ? '/ year' : '/ month',
        priceNote: 'Flat rate for residential buildings up to 20 units',
        features: BASIC_FEATURES,
        featureLabel: 'Included Capabilities',
      };

  return (
    <SideDrawer
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      title={
        isTrialing
          ? 'Complete Your Subscription'
          : isCanceling
            ? 'Subscription Ending'
            : isExpired
              ? 'Reactivate Your Subscription'
              : 'Your Plan'
      }
      description={
        isTrialing
          ? 'Subscribe to continue using Koinon after your trial ends.'
          : isCanceling
            ? 'Your subscription is scheduled to end. Reactivate to keep your workspace active.'
            : isExpired
              ? 'Your subscription is no longer active. Reactivate to restore full access.'
              : 'Manage your current subscription and billing.'
      }
      icon={<CreditCard className="w-5 h-5 text-primary" />}
    >
      <div className="space-y-6">
        {/* Billing Interval Toggle */}
        {selectedPlan === 'basic' && (isTrialing || (isActive && !isExpired)) && (
          <div className="flex items-center justify-center">
            <div className="flex items-center p-1 bg-muted/60 border border-border rounded-xl">
              <button
                type="button"
                onClick={() => setInterval('monthly')}
                className={`px-4 py-1.5 text-xs font-semibold rounded-lg transition-all cursor-pointer ${
                  interval === 'monthly'
                    ? 'bg-card text-foreground shadow-xs'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                Monthly Billing
              </button>
              <button
                type="button"
                onClick={() => setInterval('annual')}
                className={`flex items-center gap-1.5 px-4 py-1.5 text-xs font-semibold rounded-lg transition-all cursor-pointer ${
                  interval === 'annual'
                    ? 'bg-card text-foreground shadow-xs'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                <span>Annual Billing</span>
                <Badge variant="primary" size="xs">
                  Save 17%
                </Badge>
              </button>
            </div>
          </div>
        )}

        {isTrialing && (
          <div className="grid grid-cols-2 gap-2">
            <button
              type="button"
              aria-pressed={selectedPlan === 'basic'}
              onClick={() => setSelectedPlan('basic')}
              className={`rounded-lg border p-3 text-left transition-colors ${
                selectedPlan === 'basic'
                  ? 'border-primary bg-primary/5'
                  : 'border-border/80 bg-background hover:border-primary/50'
              }`}
            >
              <span className="block text-xs font-semibold text-foreground">Basic</span>
              <span className="block text-[11px] text-muted-foreground mt-0.5">€10/month · 20 units</span>
            </button>
            <button
              type="button"
              aria-pressed={selectedPlan === 'pro'}
              onClick={() => setSelectedPlan('pro')}
              className={`rounded-lg border p-3 text-left transition-colors ${
                selectedPlan === 'pro'
                  ? 'border-primary bg-primary/5'
                  : 'border-border/80 bg-background hover:border-primary/50'
              }`}
            >
              <span className="block text-xs font-semibold text-foreground">Portfolio Pro</span>
              <span className="block text-[11px] text-muted-foreground mt-0.5">€1/unit/month · €50 minimum</span>
            </button>
          </div>
        )}

        {/* Plan Card */}
        <GlassCard className="p-5 flex flex-col justify-between border border-border/80">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-primary/10 border border-primary/20 flex items-center justify-center text-primary">
                  {planConfig.icon}
                </div>
                <div>
                  <h3 className="text-sm font-bold text-foreground">{planConfig.name}</h3>
                  <p className="text-[11px] text-muted-foreground">{planConfig.subtitle}</p>
                </div>
              </div>
              {isActive && !isCanceling && (
                <Badge variant="success" size="xs">
                  Current Plan
                </Badge>
              )}
              {isCanceling && (
                <Badge variant="warning" size="xs">
                  Ending Soon
                </Badge>
              )}
              {isExpired && (
                <Badge variant="danger" size="xs">
                  Inactive
                </Badge>
              )}
              {isTrialing && (
                <Badge variant="primary" size="xs" dot>
                  Trial
                </Badge>
              )}
            </div>

            <div>
              <div className="flex items-baseline gap-1">
                <span className="text-2xl font-bold font-mono tracking-tight text-foreground">
                  {planConfig.price}
                </span>
                <span className="text-xs text-muted-foreground">{planConfig.priceUnit}</span>
              </div>
              <p className="text-[11px] text-muted-foreground mt-0.5">{planConfig.priceNote}</p>
            </div>

            <div className="pt-2 border-t border-border/60 space-y-2">
              <p className="text-[10.5px] uppercase font-bold text-muted-foreground tracking-wider">
                {planConfig.featureLabel}
              </p>
              <ul className="space-y-2">
                {planConfig.features.map((feat, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-xs text-foreground/90">
                    <Check className="w-3.5 h-3.5 text-emerald-500 shrink-0 mt-0.5" />
                    <span className="leading-snug">{feat}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="pt-6 mt-auto space-y-2">
            {isCanceling && (
              <p className="text-[11px] text-amber-600 dark:text-amber-400 text-center">
                Your subscription is scheduled to end on{' '}
                {billing?.current_period_end
                  ? new Date(billing.current_period_end).toLocaleDateString(undefined, {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                    })
                  : 'the end of your billing period'}
                .
              </p>
            )}
            {isTrialing && (
              <Button
                variant="primary"
                className="w-full justify-center h-10 text-xs sm:text-[13px] font-semibold whitespace-nowrap"
                leftIcon={<Sparkles className="w-4 h-4" />}
                isLoading={loading}
                onClick={handleCheckout}
              >
                Subscribe to {planConfig.name}
              </Button>
            )}
            {isActive && isBasic && interval !== billing?.billing_interval && (
              <Button
                variant="primary"
                className="w-full justify-center h-10 text-xs sm:text-[13px] font-semibold whitespace-nowrap"
                leftIcon={<ArrowLeftRight className="w-4 h-4" />}
                isLoading={customerPortalMutation.isPending}
                onClick={handleManageSubscription}
              >
                Switch to {interval === 'annual' ? 'Annual' : 'Monthly'}
              </Button>
            )}
            {(isCanceling || isExpired) && (
              <Button
                variant="primary"
                className="w-full justify-center h-10 text-xs sm:text-[13px] font-semibold whitespace-nowrap"
                leftIcon={<CreditCard className="w-4 h-4" />}
                isLoading={customerPortalMutation.isPending}
                onClick={handleManageSubscription}
              >
                Reactivate Subscription
              </Button>
            )}
            {(isActive || isCanceling) && (
              <Button
                variant="outline"
                className="w-full justify-center h-10 text-xs sm:text-[13px] font-semibold whitespace-nowrap"
                leftIcon={<ExternalLink className="w-4 h-4" />}
                isLoading={customerPortalMutation.isPending}
                onClick={handleManageSubscription}
              >
                Manage Subscription
              </Button>
            )}
          </div>
        </GlassCard>

        {/* Trust & Guarantee Banner */}
        <div className="p-3.5 rounded-xl bg-card border border-border/70 flex items-center gap-3">
          <ShieldCheck className="w-5 h-5 text-emerald-500 shrink-0" />
          <p className="text-[11px] text-muted-foreground leading-relaxed">
            Encrypted payments powered by <strong className="text-foreground">Stripe</strong>. Manage payment methods, download VAT receipts, or cancel anytime directly from your customer portal.
          </p>
        </div>
      </div>
    </SideDrawer>
  );
};
